import Cookies from 'js-cookie'
import { filterScreensByStudentGender } from './filterScreensByGender'
import { normalizeScreenGroups, flattenScreens } from './normalizeScreens'
import { SCREEN_ROUTE_ALIASES } from './routeAliases'
import { isScreenMarkedVisible } from './screenVisibility'

export {
  findScreenByRoute,
  flattenScreens,
  normalizeScreen,
  normalizeScreenGroups,
} from './normalizeScreens'
export { getScreenPermissions } from './screenPermissions'
export { isScreenMarkedVisible } from './screenVisibility'

export const SCREENS_UPDATED_EVENT = 'screens-updated'

const SCREENS_KEY = 'screens'
const COOKIE_EXPIRES_DAYS = 90

export const saveScreens = (screens, user) => {
  const normalized = filterScreensByStudentGender(
    normalizeScreenGroups(screens)
      .map((group) => ({
        ...group,
        screens: (group.screens || []).filter(isScreenMarkedVisible),
      }))
      .filter((group) => (group.screens || []).length > 0),
    user,
  )
  const json = JSON.stringify(normalized)

  localStorage.setItem(SCREENS_KEY, json)
  Cookies.set(SCREENS_KEY, json, { expires: COOKIE_EXPIRES_DAYS })
  window.dispatchEvent(new CustomEvent(SCREENS_UPDATED_EVENT, { detail: normalized }))

  return normalized
}

export const loadScreens = () => {
  try {
    const stored = localStorage.getItem(SCREENS_KEY)
    if (stored) return JSON.parse(stored)
  } catch (e) {
    console.error('Failed to parse stored screens from localStorage', e)
  }

  try {
    const cookieStored = Cookies.get(SCREENS_KEY)
    if (cookieStored) {
      const parsed = JSON.parse(cookieStored)
      localStorage.setItem(SCREENS_KEY, cookieStored)
      return parsed
    }
  } catch (e) {
    console.error('Failed to parse stored screens from cookies', e)
  }

  return []
}

const pickAccessibleScreen = (screen) => (screen && isScreenMarkedVisible(screen) ? screen : null)

export const getScreenByRoute = (route) => {
  const screens = flattenScreens(loadScreens())
  const normalized = String(route || '')
    .replace(/\/+$/, '')
    .replace(/\/index$/i, '')
  const matchRoute = (screenRoute) => {
    const value = String(screenRoute || '').replace(/\/+$/, '')
    return value === route || value === normalized
  }

  const direct = pickAccessibleScreen(screens.find((screen) => matchRoute(screen.screen_route)))
  if (direct) return direct

  const canonical = SCREEN_ROUTE_ALIASES[route] || SCREEN_ROUTE_ALIASES[normalized]
  if (canonical) {
    const byCanonical = pickAccessibleScreen(
      screens.find((screen) => screen.screen_route === canonical),
    )
    if (byCanonical) return byCanonical
  }

  for (const [legacyRoute, canonicalRoute] of Object.entries(SCREEN_ROUTE_ALIASES)) {
    if (canonicalRoute === route || canonicalRoute === normalized) {
      const byLegacy = pickAccessibleScreen(
        screens.find((screen) => screen.screen_route === legacyRoute),
      )
      if (byLegacy) return byLegacy
    }
  }

  const formParent = String(route || '').replace(/\/form\/?$/, '')
  if (formParent && formParent !== route) {
    const byParent = pickAccessibleScreen(
      screens.find((screen) => screen.screen_route === formParent),
    )
    if (byParent) return byParent
  }

  const financeReportsParent = '/financials/reports'
  if (normalized === financeReportsParent || normalized.startsWith(`${financeReportsParent}/`)) {
    const byReports = pickAccessibleScreen(
      screens.find(
        (screen) => String(screen.screen_route || '').replace(/\/+$/, '') === financeReportsParent,
      ),
    )
    if (byReports) return byReports
  }

  const communicationParents = ['/student/communication', '/professor/communication']
  for (const parent of communicationParents) {
    if (normalized === parent || normalized.startsWith(`${parent}/`)) {
      const byParent = pickAccessibleScreen(
        screens.find((screen) => String(screen.screen_route || '').replace(/\/+$/, '') === parent),
      )
      if (byParent) return byParent
    }
  }

  return null
}

export const clearScreens = () => {
  localStorage.removeItem(SCREENS_KEY)
  Cookies.remove(SCREENS_KEY)
}

/** @deprecated Use loadScreens */
export const getStoredScreens = loadScreens
